import type { components } from '@multaqa/api-client'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ReportSearch } from './api'
import { reportSearchFrom } from './report-search'

export type MonthlyStatement = components['schemas']['MonthlyStatementResource']
export type MonthlyStatementLine = components['schemas']['MonthlyStatementLineResource']

export const STATEMENTS = ['profit_and_loss', 'cash_flow', 'balance_sheet'] as const
export type StatementKind = (typeof STATEMENTS)[number]

export type MonthlyStatementSearch = { statement?: StatementKind; from?: string; to?: string }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * A statement screen's URL search: its own filters plus the view. `view=month`
 * shows one column per month (QuickBooks "Display columns by: Months"); no
 * view is the period total.
 */
export type StatementSearch = ReportSearch & { view?: 'month' }

export function statementSearchFrom(search: Record<string, unknown>): StatementSearch {
  return { ...reportSearchFrom(search), view: search.view === 'month' ? 'month' : undefined }
}

/** Each statement's own screen; its By month view replaced the separate screen. */
export const STATEMENT_SCREENS = {
  profit_and_loss: '/accounting/reports/profit-and-loss',
  cash_flow: '/accounting/reports/cash-flow',
  balance_sheet: '/accounting/reports/balance-sheet',
} as const satisfies Record<StatementKind, string>

/** Where an old statements-by-month bookmark now opens: the same statement, by month. */
export function monthlyStatementsRedirect(search: MonthlyStatementSearch) {
  return {
    to: STATEMENT_SCREENS[search.statement ?? 'profit_and_loss'],
    search: { from: search.from, to: search.to, view: 'month' as const },
  }
}

/** Narrow raw URL search params to the screen's own. */
export function monthlyStatementSearchFrom(
  search: Record<string, unknown>,
): MonthlyStatementSearch {
  const statement = STATEMENTS.find((value) => value === search.statement)
  const from =
    typeof search.from === 'string' && ISO_DATE.test(search.from) ? search.from : undefined
  const to = typeof search.to === 'string' && ISO_DATE.test(search.to) ? search.to : undefined
  return { statement, from, to }
}

/** A line's amount per month; the contract publishes the list loosely. */
export function lineAmounts(line: MonthlyStatementLine): (string | null)[] {
  return line.amounts.map((amount) => (typeof amount === 'string' ? amount : null))
}

export function useMonthlyStatement(
  companyId: string | null,
  statement: StatementKind,
  from: string,
  to: string,
) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'monthly-statements', statement, from, to],
    enabled: Boolean(companyId) && from <= to,
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<MonthlyStatement> => {
      const response = await api.GET('/accounting/reports/monthly-statements/{statement}', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { path: { statement }, query: { from, to } },
      })
      if (response.error) throw response.error
      return response.data.data
    },
  })
}
